import React, { useState } from "react";
import { FiUser, FiAlertTriangle, FiAward, FiActivity } from "react-icons/fi";
import { MiniBar, healthColor } from "./fleetHelpers";

const BADGE_STYLE = {
  "Top Driver":     { bg: "#d1fae5", color: "#059669" },
  "Safe Driver":    { bg: "#dbeafe", color: "#2563eb" },
  "Good Driver":    { bg: "#ede9fe", color: "#7c3aed" },
  "Average":        { bg: "#fef3c7", color: "#d97706" },
  "Below Average":  { bg: "#fee2e2", color: "#dc2626" },
  "Needs Coaching": { bg: "#fce7f3", color: "#be185d" },
};

const DriverPanel = ({ d }) => {
  const [selected, setSelected] = useState(null);
  const sorted = [...d.drivers].sort((a, b) => b.score - a.score);
  const selectedDriver = selected ? d.drivers.find(dr => dr.id === selected) : null;
  const selectedVehicle = selectedDriver ? d.vehicles.find(v => v.id === selectedDriver.vehicleId) : null;

  return (
    <div className="fleet-drivers">

      {/* ── Summary Chips ── */}
      <div className="dr-summary-row">
        <div className="dr-sum-chip" style={{ background: "#d1fae5" }}>
          <FiAward size={16} color="#059669" />
          <span style={{ color: "#059669" }}>
            {sorted.filter(dr => dr.score >= 85).length} Top Performers
          </span>
        </div>
        <div className="dr-sum-chip" style={{ background: "#fee2e2" }}>
          <FiAlertTriangle size={16} color="#dc2626" />
          <span style={{ color: "#dc2626" }}>
            {sorted.filter(dr => dr.score < 65).length} Need Coaching
          </span>
        </div>
        <div className="dr-sum-chip" style={{ background: "#dbeafe" }}>
          <FiActivity size={16} color="#2563eb" />
          <span style={{ color: "#2563eb" }}>
            {d.drivers.reduce((acc, dr) => acc + dr.kmToday, 0).toLocaleString()} km Today
          </span>
        </div>
        <div className="dr-sum-chip" style={{ background: "#fef3c7" }}>
          <FiActivity size={16} color="#d97706" />
          <span style={{ color: "#d97706" }}>
            {d.drivers.reduce((acc, dr) => acc + dr.trips, 0)} Trips Today
          </span>
        </div>
      </div>

      <div className="dr-layout">

        {/* ── Driver Leaderboard ── */}
        <div className="dr-list">
          {sorted.map((dr, i) => {
            const bs = BADGE_STYLE[dr.badge] || { bg: "#f1f5f9", color: "#475569" };
            const isActive = selected === dr.id;
            return (
              <div
                key={dr.id}
                className={`dr-card ${isActive ? "dr-card-active" : ""}`}
                onClick={() => setSelected(isActive ? null : dr.id)}
              >
                <div className="dr-rank" style={{ color: i < 3 ? "#f59e0b" : "#94a3b8" }}>
                  {i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}
                </div>

                <div className="dr-avatar" style={{ background: `${healthColor(dr.score)}22`, color: healthColor(dr.score) }}>
                  <FiUser size={18} />
                </div>

                <div className="dr-info">
                  <div className="dr-name">{dr.name}</div>
                  <div className="dr-vehicle">{dr.vehicleId}</div>
                  <div className="dr-badges-row">
                    <span className="dr-badge" style={{ background: bs.bg, color: bs.color }}>{dr.badge}</span>
                  </div>
                </div>

                <div className="dr-score-col">
                  <div className="dr-score" style={{ color: healthColor(dr.score) }}>{dr.score}</div>
                  <MiniBar value={dr.score} color={healthColor(dr.score)} height={5} />
                  <div className="dr-trips">{dr.trips} trips · {dr.kmToday} km</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Driver Detail ── */}
        <div className="dr-detail">
          {selectedDriver ? (
            <>
              <div className="dr-detail-header">
                <div className="dr-detail-avatar" style={{ background: `${healthColor(selectedDriver.score)}22`, color: healthColor(selectedDriver.score) }}>
                  <FiUser size={28} />
                </div>
                <div>
                  <div className="dr-detail-name">{selectedDriver.name}</div>
                  <div className="dr-detail-sub">{selectedDriver.vehicleId} · Driver ID: {selectedDriver.id}</div>
                  <span
                    className="dr-badge"
                    style={{ background: BADGE_STYLE[selectedDriver.badge]?.bg, color: BADGE_STYLE[selectedDriver.badge]?.color }}
                  >
                    {selectedDriver.badge}
                  </span>
                </div>
              </div>

              {/* Score Ring */}
              <div className="dr-score-section">
                <div className="dr-score-big" style={{ color: healthColor(selectedDriver.score) }}>
                  {selectedDriver.score}
                </div>
                <div className="dr-score-label">Safety Score</div>
                <MiniBar value={selectedDriver.score} color={healthColor(selectedDriver.score)} height={8} />
              </div>

              {/* Behaviour Breakdown */}
              <div className="dr-behavior-title">Behaviour Metrics (Today)</div>
              <div className="dr-behavior-grid">
                <div className={`dr-beh-item ${selectedDriver.harshBraking > 3 ? "dr-beh-bad" : ""}`}>
                  <span className="dr-beh-label">Harsh Braking</span>
                  <span className="dr-beh-val">{selectedDriver.harshBraking}</span>
                </div>
                <div className={`dr-beh-item ${selectedDriver.rapidAccel > 3 ? "dr-beh-bad" : ""}`}>
                  <span className="dr-beh-label">Rapid Acceleration</span>
                  <span className="dr-beh-val">{selectedDriver.rapidAccel}</span>
                </div>
                <div className={`dr-beh-item ${selectedDriver.overspeed > 2 ? "dr-beh-bad" : ""}`}>
                  <span className="dr-beh-label">Overspeed Events</span>
                  <span className="dr-beh-val">{selectedDriver.overspeed}</span>
                </div>
                <div className={`dr-beh-item ${selectedDriver.nightDriving > 0 ? "dr-beh-warn" : ""}`}>
                  <span className="dr-beh-label">Night Driving</span>
                  <span className="dr-beh-val">{selectedDriver.nightDriving}</span>
                </div>
                <div className={`dr-beh-item ${selectedDriver.idleExcess > 30 ? "dr-beh-bad" : selectedDriver.idleExcess > 15 ? "dr-beh-warn" : ""}`}>
                  <span className="dr-beh-label">Idle Excess (min)</span>
                  <span className="dr-beh-val">{selectedDriver.idleExcess}</span>
                </div>
                <div className="dr-beh-item">
                  <span className="dr-beh-label">KM Today</span>
                  <span className="dr-beh-val">{selectedDriver.kmToday}</span>
                </div>
              </div>

              {/* Vehicle link */}
              {selectedVehicle && (
                <div className="dr-vehicle-link">
                  <div className="dr-vl-title">Assigned Vehicle</div>
                  <div className="dr-vl-row">
                    <span>{selectedVehicle.name} ({selectedVehicle.plateNo})</span>
                    <span style={{ color: "#10b981" }}>Health: {selectedVehicle.healthScore}%</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="dr-empty">
              <FiUser size={40} color="#cbd5e1" />
              <p>Select a driver to view detailed analytics</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverPanel;
